import {TweenMax} from 'gsap';
import {noop as _noop} from 'lodash/util';
import {assign as _extend} from 'lodash/object';
import sound from './Sound';
import Character from './Character';


class Bus extends Character {
  /**
   * Bus Constructor
   * @param options
   * @param {String} options.spritesheet The object property to ask PIXI's resource loader for
   * @param {PIXI.Point} options.downPoint The point where the Bus should sit at during active play
   * @param {PIXI.Point} options.upPoint The point the Bus should rise to when retrieving trams
   */
  constructor(options) {
    const states = [
      {
        name: 'double',
        animationSpeed: 0.1
      },
      {
        name: 'single',
        animationSpeed: 0.1
      },
      {
        name: 'sad',
        animationSpeed: 0.1
      },
      {
        name: 'pride',
        animationSpeed: 0.1
      }
    ];
    super('bus', options.spritesheet, states);
    this.toRetrieve = 0;
    this.anchor.set(0.5, 0);
    this.options = options;
    this.sniffSoundId = null;
  }

  /**
   * pride
   * @param opts
   * @param {PIXI.Point} [opts.startPoint=this.position] Point the bus should start parade from
   * @param {PIXI.Point} [opts.endPoint=this.position] Point the bus should parade to
   * @param {Function} [opts.onStart=_noop] Function to call at the start of the bus parade
   * @param {Function} [opts.onComplete=_noop] Function to call once the bus has finished parade
   * @returns {Bus}
   */
  pride(opts) {
    const options = _extend({
      startPoint: this.position,
      endPoint: this.position,
      onStart: _noop,
      onComplete: _noop
    }, opts);

    this.sit({
      point: options.startPoint,
      pre: () => {
        this.visible = false;
      }
    });

    this.timeline.to(this.position, 3, {
      x: options.endPoint.x,
      y: options.endPoint.y,
      ease: 'Linear.easeNone',
      onStart: () => {
        this.visible = true;
        this.parent.setChildIndex(this, this.parent.children.length - 1);
        this.state = 'pride';
        options.onStart();
      },
      onComplete: () => {
        options.onComplete();
      }
    });

    return this;
  }

  /**
   * upDownTween
   * @param opts
   * @param {PIXI.Point} [opts.startPoint] Lowest point the bus should go to, and where the animation starts
   * @param {PIXI.Point} [opts.endPoint] Highest point the bus should go to
   * @param {Function} [opts.onStart] Function to call at the start of the up/down animation
   * @param {Function} [opts.onComplete] Function to call once the bus has completed an up/down cycle
   * return {Bus}
   */
  upDownTween(opts) {
    const options = _extend({
      startPoint: this.options.downPoint || this.position,
      endPoint: this.options.upPoint || this.position,
      onStart: _noop,
      onComplete: _noop
    }, opts);

    this.sit({
      point: options.startPoint
    });

    this.timeline.add(TweenMax.to(this.position, 0.4, {
      y: options.endPoint.y,
      yoyo: true,
      repeat: 1,
      repeatDelay: 0.5,
      ease: 'Linear.easeNone',
      onStart: () => {
        this.visible = true;
        options.onStart.call(this);
      },
      onComplete: options.onComplete
    }));
    return this;
  }

  /**
   * sit
   * @param opts
   * @param {PIXI.Point} [opts.point] Point the bus will go to without animation
   * @param {Function} [opts.onStart] Function called before moving the bus
   * @param {Function} [opts.onComplete] Function called after the bus has moved
   * @returns {Bus}
   */
  sit(opts) {
    const options = _extend({
      point: this.position,
      onStart: _noop,
      onComplete: _noop
    }, opts);

    this.timeline.add(() => {
      options.onStart();
      this.position.set(options.point.x, options.point.y);
      options.onComplete();
    });
    return this;
  }

  /**
   * retrieve
   * @retuns {Bus}
   */
  retrieve() {
    this.toRetrieve++;

    this.upDownTween({
      onStart: () => {
        if (this.toRetrieve >= 2) {
          this.state = 'double';
          this.toRetrieve-=2;
        } else if (this.toRetrieve === 1) {
          this.state = 'single';
          this.toRetrieve-=1;
        }
      }
    });
    return this;
  }

  /**
   * sad
   * @returns {Bus}
   */
  sad() {
    this.upDownTween({
      state: 'sad',
      onStart: () => {
        this.toRetrieve = 0;
        this.state = 'sad';
        sound.play('sad');
      }
    });

    return this;
  }

  /**
   * isActive
   * @returns {boolean}
   */
  isActive() {
    return super.isActive() && this.toRetrieve > 0;
  }
}

export default Bus;



// WEBPACK FOOTER //
// ./src/modules/Bus.js